/**
 * Ids for local documents (not cryptographic). Ported from the original `model/ids.ts`;
 * the clock, random source and counter are injectable so tests and fixtures are
 * deterministic. Ids are opaque: never parse them.
 *
 * Format: `<prefix>-<base36(now)>-<base36(counter)>-<random4>` where
 * `random4 = random().toString(36).slice(2, 6)` (up to 4 base-36 chars).
 */
export interface UidSource {
  now: () => number;
  random: () => number;
}

export type Uid = (prefix: string) => string;

/** One id from explicit parts (the original formula). */
export function formatUid(prefix: string, now: number, counter: number, random: number): string {
  return `${prefix}-${now.toString(36)}-${counter.toString(36)}-${random.toString(36).slice(2, 6)}`;
}

/** A uid generator with its own counter (module-level in the original; starts at 0, first id uses 1). */
export function createUid(source: UidSource = { now: Date.now, random: Math.random }): Uid {
  let counter = 0;
  return (prefix: string) => {
    counter = (counter + 1) % 1_000_000;
    return formatUid(prefix, source.now(), counter, source.random());
  };
}

/** Default generator: wall clock + Math.random, like the original. */
export const uid: Uid = createUid();

/** Deterministic generator that hands out the given ids in order (fixtures, tests). */
export function queuedUid(ids: readonly string[]): Uid {
  let i = 0;
  return (prefix: string) => {
    if (i >= ids.length) throw new Error(`queuedUid: ran out of ids (wanted a '${prefix}' id)`);
    return ids[i++];
  };
}
